/**
 * Object Type Sidebar
 * Manages object type definitions (CCTV, Charger, ParkingSpot, etc.)
 */

import { useEffect } from 'react'
import { useObjectTypeStore } from '../../../../map-editor-shared/store/objectTypeStore'
import { TypeListItem } from '../components'
import styles from './ObjectTypeSidebar.module.css'
import { log, LogCategory } from '../../../../map-editor-shared/lib/logger'
import { useObjectTypeForm } from './hooks/useObjectTypeForm'
import { useAssetUpload } from './hooks/useAssetUpload'
import { usePropertyEditor } from './hooks/usePropertyEditor'
import { ObjectTypeFormFields } from './components/ObjectTypeFormFields'
import type { ObjectTypeSidebarProps } from './types'

export type { ObjectTypeSidebarProps }

export function ObjectTypeSidebar({ onSelectType, selectedTypeId, projectId }: ObjectTypeSidebarProps) {
  const types = useObjectTypeStore(state => state.types)
  const updateType = useObjectTypeStore(state => state.updateType)

  // Migration: Fix types where icon field contains color codes
  useEffect(() => {
    let migrated = false
    types.forEach(type => {
      if (type.icon && type.icon.startsWith('#')) {
        log.warn(LogCategory.STATE, `Migrating type ${type.name}: moving color from icon to color field`, {
          oldIcon: type.icon,
          oldColor: type.color
        })
        updateType(type.id, {
          icon: '', // Clear invalid icon
          color: type.icon // Move color code to correct field
        })
        migrated = true
      }
    })
    if (migrated) {
      log.info(LogCategory.STATE, 'Icon/color migration completed - please re-upload CSV to apply changes')
    }
  }, []) // Run once on mount

  const form = useObjectTypeForm()

  const assetUpload = useAssetUpload({
    setIcon: (icon: string) => form.setFormData(prev => ({ ...prev, icon })),
  })

  const propertyEditor = usePropertyEditor(form.formData, form.setFormData)

  const resetAsset = () => {
    assetUpload.resetAsset()
  }

  const renderForm = (isEditing: boolean) => (
    <ObjectTypeFormFields
      isEditing={isEditing}
      editingId={form.editingId}
      formData={form.formData}
      setFormData={form.setFormData}
      previewUrl={assetUpload.previewUrl}
      assetFile={assetUpload.assetFile}
      showIconSelector={assetUpload.showIconSelector}
      setShowIconSelector={assetUpload.setShowIconSelector}
      fileInputRef={assetUpload.fileInputRef}
      onFileChange={assetUpload.handleFileChange}
      onRemoveFile={assetUpload.removeFile}
      onIconSelect={assetUpload.handleIconSelect}
      onAddProperty={propertyEditor.addProperty}
      onUpdateProperty={propertyEditor.updateProperty}
      onRemoveProperty={propertyEditor.removeProperty}
      onSave={isEditing ? () => form.handleUpdate(form.editingId!, resetAsset) : () => form.handleAdd(resetAsset)}
      onCancel={() => form.cancelEdit(resetAsset)}
      onColorSync={isEditing && form.editingId ? (updates) => updateType(form.editingId!, updates) : undefined}
      showJson={form.showJson}
      setShowJson={form.setShowJson}
      getPreviewData={propertyEditor.getPreviewData}
    />
  )

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>객체 타입</h3>
        {!form.showAddForm && !form.editingId && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="file"
              ref={form.jsonInputRef}
              onChange={form.handleJsonUpload}
              accept=".json"
              style={{ display: 'none' }}
            />
            <button
              className={styles.addButton}
              onClick={() => form.jsonInputRef.current?.click()}
              style={{ backgroundColor: 'var(--local-surface-hover)', color: 'var(--local-text)' }}
              title="JSON 가져오기"
            >
              Import
            </button>
            <button
              className={styles.addButton}
              onClick={() => {
                form.handleShowAddForm()
                assetUpload.resetAsset()
              }}
            >
              + 추가
            </button>
          </div>
        )}
      </div>

      {(form.error || assetUpload.error) && (
        <div className={styles.error}>{form.error || assetUpload.error}</div>
      )}

      {form.successMessage && (
        <div style={{
          padding: '12px',
          marginBottom: '16px',
          background: 'rgba(34, 197, 94, 0.1)',
          color: '#4ade80',
          border: '1px solid rgba(34, 197, 94, 0.2)',
          borderRadius: '6px',
          fontSize: '13px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {form.successMessage}
        </div>
      )}

      {/* Add Form */}
      {form.showAddForm && renderForm(false)}

      {/* Type List */}
      <div className={styles.typeList}>
        {types.map((type) => (
          <div key={type.id}>
            {form.editingId === type.id ? (
              <div className={`${styles.typeItem} ${styles.selected}`}>
                {renderForm(true)}
              </div>
            ) : (
              <TypeListItem
                type={type}
                isSelected={selectedTypeId === type.id}
                onSelect={() => onSelectType?.(type)}
                onEdit={() => form.startEdit(type, assetUpload.initPreviewFromIcon)}
                onDelete={() => form.handleDelete(type.id)}
                onPriorityChange={form.handlePriorityChange}
                typeItemClassName={styles.typeItem}
                selectedClassName={styles.selected}
                typeHeaderClassName={styles.typeHeader}
                typeNameClassName={styles.typeName}
                typeActionsClassName={styles.typeActions}
                editButtonClassName={styles.editButton}
                deleteButtonClassName={styles.deleteButton}
              />
            )}
          </div>
        ))}
      </div>

      {types.length === 0 && !form.showAddForm && (
        <div className={styles.empty}>
          객체 타입이 없습니다.<br />
          + 추가 버튼을 눌러 타입을 생성하세요.
        </div>
      )}

      {/* Zone 별도 진입점 제거 — '존' ObjectType 클릭으로 통합됨.
          (handleTypeSelect 가 isZoneObjectType(type) 일 때 drawing store start) */}
    </div>
  )
}
